import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiEdit2, FiSave } from 'react-icons/fi';
import { LedgerAccountSelect } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { createJournalEntry, getAllLedgerAccounts, getLedgerBalanceMap } from '../../services/api';
import { today } from '../../utils/dateUtils';
import { getCurrentUnlockedFinancialYear, getUnlockedFinancialYears } from '../../utils/financialYears';
import rStyles from '../Receipts/NewReceiptsPage.module.css';
import jStyles from './JournalEntries.module.css';

import { fmtCurrency } from '../../utils/formatNumber';

/** Prevent mouse-wheel scroll from changing a focused number input's value */
const blockWheel = (e) => e.target.blur();

const GROUP_LABELS  = { asset: 'Assets', liability: 'Liabilities', equity: 'Equity', income: 'Income', expenditure: 'Expenses' };
const NATURE_COLORS = { asset: '#3b82f6', liability: '#ef4444', equity: '#8b5cf6', income: '#10b981', expenditure: '#f59e0b' };

const TRANSACTION_TYPES = [
  {
    value: 'loan_received',    label: 'Loan Received',
    hint: { dr: 'Bank Account', cr: 'Loan Payable' },
    drFilter: (a) => a.group?.nature === 'asset',
    crFilter: (a) => a.group?.nature === 'liability',
  },
  {
    value: 'loan_repayment',   label: 'Loan Repayment',
    hint: { dr: 'Loan Payable', cr: 'Bank Account' },
    drFilter: (a) => a.group?.nature === 'liability',
    crFilter: (a) => a.group?.nature === 'asset',
  },
  {
    value: 'loan_given',       label: 'Loan Given',
    hint: { dr: 'Loans Given (Asset)', cr: 'Cash / Bank' },
    drFilter: (a) => a.group?.nature === 'asset',
    crFilter: (a) => ['cash', 'bank'].includes(a.accountType),
  },
  {
    value: 'asset_purchase',   label: 'Asset Purchase',
    hint: { dr: 'Fixed Asset', cr: 'Bank / Cash' },
    drFilter: (a) => a.group?.nature === 'asset',
    crFilter: (a) => ['cash', 'bank'].includes(a.accountType),
  },
  {
    value: 'fund_transfer',    label: 'Bank Fund Transfer',
    hint: { dr: 'Target Bank', cr: 'Source Bank' },
    drFilter: (a) => a.accountType === 'bank',
    crFilter: (a) => a.accountType === 'bank',
  },
  {
    value: 'cash_deposit',     label: 'Cash Deposit to Bank',
    hint: { dr: 'Bank Account', cr: 'Cash in Hand' },
    drFilter: (a) => a.accountType === 'bank',
    crFilter: (a) => a.accountType === 'cash',
  },
  {
    value: 'cash_withdrawal',  label: 'Cash Withdrawal from Bank',
    hint: { dr: 'Cash in Hand', cr: 'Bank Account' },
    drFilter: (a) => a.accountType === 'cash',
    crFilter: (a) => a.accountType === 'bank',
  },
  {
    value: 'contra',           label: 'Contra / Adjustment',
    hint: { dr: 'Any Account', cr: 'Any Account' },
    drFilter: () => true,
    crFilter: () => true,
  },
  {
    value: 'donation_transfer', label: 'Donation Balance Transfer',
    hint: { dr: 'Source Income Account', cr: 'Target Income Account' },
    drFilter: (a) => a.subGroup?.name === 'Donation Income',
    crFilter: (a) => a.subGroup?.name === 'Donation Income',
  },
];

const AccountBadge = ({ accountId, accounts, balanceMap, fyReady }) => {
  const acc = accounts.find((a) => String(a.id) === String(accountId));
  if (!acc) return null;
  const nature = acc.group?.nature;
  const subGroup = acc.subGroup?.name;
  // Show zero when FY is selected but account has no entries yet
  const rawBal = balanceMap ? balanceMap[String(accountId)] : undefined;
  const bal = fyReady && rawBal === undefined ? 0 : rawBal;
  const hasBal = bal !== undefined && bal !== null;
  const balSide = hasBal ? (bal >= 0 ? 'Dr' : 'Cr') : null;
  const balColor = balSide === 'Dr' ? '#3b82f6' : '#ef4444';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: '0.75rem', marginTop: 4,
      color: NATURE_COLORS[nature] || 'var(--color-text-muted)',
    }}>
      <span style={{ fontWeight: 600 }}>{GROUP_LABELS[nature] || nature}</span>
      {subGroup && <span style={{ color: 'var(--color-text-muted)' }}>· {subGroup}</span>}
      {hasBal && (
        <>
          <span style={{ color: 'var(--color-text-muted)' }}>·</span>
          <span style={{ fontWeight: 700, color: balColor }}>
            {balSide} {fmtCurrency(Math.abs(bal))}
          </span>
        </>
      )}
    </span>
  );
};


const EMPTY = {
  financialYearId: '',
  entryDate:       today(),
  narration:       '',
  debitAccountId:  '',
  creditAccountId: '',
  amount:          '',
};

const NewJournalEntryPage = () => {
  const {
    activeDerasarId,
    visibleFinancialYears,
    activeFinancialYearId,
  } = useAuth();
  const navigate = useNavigate();

  const [form,            setForm]            = useState(EMPTY);
  const [transactionType, setTransactionType] = useState('');
  const [years,           setYears]           = useState([]);
  const [accounts,        setAccounts]        = useState([]);
  const [balanceMap,      setBalanceMap]      = useState({});
  const [fyReady,         setFyReady]         = useState(false);
  const [saving,          setSaving]          = useState(false);

  const selectableYears = useMemo(
    () => getUnlockedFinancialYears(visibleFinancialYears || []),
    [visibleFinancialYears],
  );
  const selectedFinancialYear = years.find((financialYear) => String(financialYear.id) === String(form.financialYearId)) || null;
  const minEntryDate = (selectedFinancialYear?.startDate || '').slice(0, 10);
  const maxEntryDate = (selectedFinancialYear?.endDate || '').slice(0, 10);

  useEffect(() => {
    if (!activeDerasarId) return;
    Promise.all([
      Promise.resolve({ data: selectableYears }),
      getAllLedgerAccounts(activeDerasarId),
    ]).then(([yRes, aRes]) => {
      setYears(yRes.data);
      setAccounts(aRes.data.filter((a) => a.isActive));
      const current = yRes.data.find((financialYear) => String(financialYear.id) === String(activeFinancialYearId))
        || getCurrentUnlockedFinancialYear(yRes.data);
      if (current) setForm((f) => ({ ...f, financialYearId: current.id }));
    }).catch(() => toast.error('Failed to load form data'));
  }, [activeDerasarId, selectableYears, activeFinancialYearId]);

  useEffect(() => {
    if (!activeDerasarId) return;
    setYears(selectableYears);
    if (activeFinancialYearId && selectableYears.some((financialYear) => String(financialYear.id) === String(activeFinancialYearId))) {
      setForm((current) => ({ ...current, financialYearId: String(activeFinancialYearId) }));
    }
  }, [activeDerasarId, selectableYears, activeFinancialYearId]);

  useEffect(() => {
    if (!activeDerasarId || !form.financialYearId) return;
    setFyReady(false);
    getLedgerBalanceMap(activeDerasarId, form.financialYearId)
      .then((res) => { setBalanceMap(res.data.data || res.data || {}); setFyReady(true); })
      .catch(() => setFyReady(false));
  }, [activeDerasarId, form.financialYearId]);

  useEffect(() => {
    if (!selectedFinancialYear) return;

    setForm((current) => {
      let nextEntryDate = current.entryDate || minEntryDate || maxEntryDate || today();
      if (minEntryDate && nextEntryDate < minEntryDate) nextEntryDate = minEntryDate;
      if (maxEntryDate && nextEntryDate > maxEntryDate) nextEntryDate = maxEntryDate;
      if (nextEntryDate === current.entryDate) return current;
      return { ...current, entryDate: nextEntryDate };
    });
  }, [selectedFinancialYear, minEntryDate, maxEntryDate]);

  const patch = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.debitAccountId || !form.creditAccountId) {
      toast.error('Please select both debit and credit accounts');
      return;
    }
    if (String(form.debitAccountId) === String(form.creditAccountId)) {
      toast.error('Debit and credit accounts must be different');
      return;
    }
    if (!selectedFinancialYear) {
      toast.error('Select an unlocked financial year');
      return;
    }
    if (minEntryDate && form.entryDate < minEntryDate) {
      toast.error('Entry date must fall within the selected financial year');
      return;
    }
    if (maxEntryDate && form.entryDate > maxEntryDate) {
      toast.error('Entry date must fall within the selected financial year');
      return;
    }
    setSaving(true);
    try {
      await createJournalEntry({
        ...form,
        derasarId: activeDerasarId,
        transactionType: transactionType || 'contra',
      });
      toast.success('Journal entry created');
      navigate('/accounting/journal-entries');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create journal entry');
    } finally {
      setSaving(false);
    }
  };

  const selectedType = TRANSACTION_TYPES.find((t) => t.value === transactionType) || null;

  const handleTypeChange = (val) => {
    setTransactionType(val);
    setForm((f) => ({ ...f, debitAccountId: '', creditAccountId: '' }));
  };

  const debitFilterFn = useMemo(
    () => selectedType ? selectedType.drFilter : () => true,
    [selectedType],
  );
  const creditFilterFn = useMemo(
    () => {
      const base = selectedType ? selectedType.crFilter : () => true;
      return (a) => base(a) && String(a.id) !== String(form.debitAccountId);
    },
    [selectedType, form.debitAccountId],
  );

  const totalAmount = parseFloat(form.amount || 0);


  return (
    <div className={jStyles.formPage}>
      <div className="page-header">
        <h2><FiEdit2 /> New Journal Entry</h2>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/accounting/journal-entries')}>
          <FiArrowLeft /> Back
        </button>
      </div>

      <div className={jStyles.formScrollArea}>

        {/* Transaction type selector */}
        <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }}>
          <div className="form-group" style={{ margin: 0, maxWidth: 360 }}>
            <label>Transaction Type</label>
            <select value={transactionType} onChange={(e) => handleTypeChange(e.target.value)}>
              <option value="">— All accounts (custom entry) —</option>
              {TRANSACTION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          {selectedType && (
            <div style={{ marginTop: 10, fontSize: '0.82rem', color: 'var(--color-text-muted)', display: 'flex', gap: 12 }}>
              <span>Dr: <strong style={{ color: '#3b82f6' }}>{selectedType.hint.dr}</strong></span>
              <span>Cr: <strong style={{ color: '#ef4444' }}>{selectedType.hint.cr}</strong></span>
            </div>
          )}
        </div>

        <form id="new-journal-entry-form" onSubmit={handleSubmit}>
          <div className="card">

            {/* -- Section 1: Entry Details -- */}
            <div className={rStyles.section}>
              <div className={rStyles.sectionLabel}>Entry Details</div>
              <div className="form-group" style={{ width: 160 }}>
                <label>Entry Date *</label>
                <input
                  type="date"
                  required
                  min={minEntryDate || undefined}
                  max={maxEntryDate || undefined}
                  value={form.entryDate}
                  onChange={(e) => patch('entryDate', e.target.value)}
                />
              </div>
            </div>

            <div className={rStyles.divider} />

            {/* -- Section 2: Accounts -- */}
            <div className={rStyles.section}>
              <div className={rStyles.sectionLabel}>Accounts</div>
              <div className={rStyles.row2}>
                <div className="form-group">
                  <label>Debit Account (Dr) *</label>
                  <LedgerAccountSelect
                    value={form.debitAccountId}
                    onChange={(id) => patch('debitAccountId', id)}
                    accounts={accounts}
                    filterFn={debitFilterFn}
                    balanceMap={balanceMap}
                    placeholder="Select account…"
                  />
                  <AccountBadge accountId={form.debitAccountId} accounts={accounts} balanceMap={balanceMap} fyReady={fyReady} />
                </div>
                <div className="form-group">
                  <label>Credit Account (Cr) *</label>
                  <LedgerAccountSelect
                    value={form.creditAccountId}
                    onChange={(id) => patch('creditAccountId', id)}
                    accounts={accounts}
                    filterFn={creditFilterFn}
                    balanceMap={balanceMap}
                    placeholder="Select account…"
                  />
                  <AccountBadge accountId={form.creditAccountId} accounts={accounts} balanceMap={balanceMap} fyReady={fyReady} />
                </div>
              </div>
            </div>

            <div className={rStyles.divider} />

            {/* -- Section 3: Amount & Narration -- */}
            <div className={rStyles.section}>
              <div className={rStyles.sectionLabel}>Amount & Narration</div>
              <div className={jStyles.amountRow}>
                <div className="form-group" style={{ width: 160, flexShrink: 0 }}>
                  <label>Amount (₹) *</label>
                  <input
                    type="number" required min="0.01" step="0.01"
                    className={rStyles.noSpin}
                    value={form.amount}
                    onWheel={blockWheel}
                    onChange={(e) => patch('amount', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Narration *</label>
                  <input
                    required
                    value={form.narration}
                    onChange={(e) => patch('narration', e.target.value)}
                    placeholder="Describe the reason for this entry…"
                  />
                </div>
              </div>
            </div>

          </div>
        </form>
      </div>

      {/* -- Footer -- */}
      <div className={jStyles.pageFooter}>
        <div className={jStyles.pageFooterLeft} />
        <div className={jStyles.pageFooterRight}>
          <div className={rStyles.totalBox}>
            <span className={rStyles.totalLabel}>Entry Amount</span>
            <span className={rStyles.totalValue}>
              {totalAmount > 0 ? fmtCurrency(totalAmount) : <span className={rStyles.totalMuted}>—</span>}
            </span>
          </div>
          <div className={rStyles.actionBtns}>
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/accounting/journal-entries')}>
              Cancel
            </button>
            <button type="submit" form="new-journal-entry-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : <><FiSave /> Create Entry</>}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default NewJournalEntryPage;
